import { Button, Flex, ModuleLayout, Space, Spacing } from '@billy/design-system'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSkatConnection } from '@features/vatDeclarations/contexts/skatConnectionContext'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { VatDeclarationContextProvider } from '../../context/vatDeclarationContext'
import { useVatDeclarations } from '../../context/vatDeclarationsContext'
import { useVatDeclarationsRoutes } from '../../context/vatDeclarationsRouteContext'
import { useSubmitToSkatForm } from '../../hooks/useSubmitToSkatForm'
import { shouldUseDKReportContent } from '../../utils/isSupportedCountryId'
import { SkatBox } from '../SkatBox'
import { SkatModule } from '../SkatModule'
import { SubmitToSkatForm } from '../VatDeclaration/elements/SubmitToSkatForm'
import { SubmitToSkatContextProvider } from '../VatDeclaration/elements/SubmitToSkatForm/context/submitToSkatContext'
import { VatDeclarationIntegrationForm } from '../VatDeclaration/elements/VatDeclarationIntegrationForm'
import { InfoSection } from './elements/InfoSection'
import { VatDeclarationStatusesInfo } from './elements/VatDeclarationsStatusesInfo'
import { VatDeclarationsTable } from './elements/VatDeclarationsTable'
import * as Styled from './styles'

export const VatDeclarationsList = () => {
  const { t } = useTranslation()
  const { isCorrectionBannerClosed, isLoadingVatDeclarations, unsettledCorrections } = useVatDeclarations()
  const { organization } = useUserOrganization()
  const { isValidCVR } = useSkatConnection()
  const vatDeclarationsRoutes = useVatDeclarationsRoutes()

  const { url: euSalesNoVatUrl } = useOrganizationViewUrl(vatDeclarationsRoutes.getEuSalesNoVatRoute())

  const [vatDeclarationId, setVatDeclarationId] = useState<string | null>(null)
  const { isSubmissionFormOpen, submissionDeclarationId, setIsSubmissionFormOpen } = useSubmitToSkatForm()

  const isSkatAvailable = organization?.countryId === 'DK'
  const shouldShowCorrectionBanner = unsettledCorrections.length > 0 && !isCorrectionBannerClosed

  const handleShowIntegrationForm = useCallback(
    (id: string) => {
      if (isSkatAvailable) {
        setIsSubmissionFormOpen(true, id)
      } else {
        setVatDeclarationId(id)
      }
    },
    [isSkatAvailable, setIsSubmissionFormOpen],
  )

  const handleIntegrationFormBack = useCallback((showForm: boolean) => {
    if (!showForm) {
      setVatDeclarationId(null)
    }
  }, [])

  const handleIntegrationFormClose = useCallback(() => {
    setVatDeclarationId(null)
  }, [])

  const handleSubmissionFormClose = useCallback(
    (isOpen: boolean) => {
      setIsSubmissionFormOpen(isOpen)
    },
    [setIsSubmissionFormOpen],
  )

  /* DK */
  if (isSubmissionFormOpen && submissionDeclarationId) {
    return (
      <VatDeclarationContextProvider vatDeclarationId={submissionDeclarationId}>
        <SubmitToSkatContextProvider
          isSubmissionFormOpen={isSubmissionFormOpen}
          declarationId={submissionDeclarationId}
        >
          <SubmitToSkatForm setIsSubmissionFormOpen={handleSubmissionFormClose} />
        </SubmitToSkatContextProvider>
      </VatDeclarationContextProvider>
    )
  }

  /* NL */
  if (vatDeclarationId) {
    return (
      <VatDeclarationContextProvider vatDeclarationId={vatDeclarationId}>
        <VatDeclarationIntegrationForm onBack={handleIntegrationFormBack} onSuccess={handleIntegrationFormClose} />
      </VatDeclarationContextProvider>
    )
  }

  /* Loading state */
  if (isLoadingVatDeclarations) {
    return (
      <SkatModule>
        <ModuleLayout title={t('vat_declarations.list.header')}>
          <VatDeclarationsTable onShowIntegrationForm={handleShowIntegrationForm} />
        </ModuleLayout>
      </SkatModule>
    )
  }

  return (
    <SkatModule>
      <ModuleLayout
        sideActions={
          shouldUseDKReportContent(organization?.countryId) && (
            <Button className="button" to={euSalesNoVatUrl} variant="outlined">
              {t('vat_declarations.list.eu_sales_without_vat.button.label')}
            </Button>
          )
        }
        title={t('vat_declarations.list.header')}
      >
        <InfoSection
          isSkatAvailable={isSkatAvailable}
          isValidCVR={isValidCVR}
          shouldShowCorrectionBanner={shouldShowCorrectionBanner}
        />
        <Flex gap={Spacing.S32}>
          <Styled.VatDeclarationsTableWrapper>
            <VatDeclarationsTable onShowIntegrationForm={handleShowIntegrationForm} />
          </Styled.VatDeclarationsTableWrapper>
          {isSkatAvailable ? <SkatBox /> : null}
        </Flex>
        <Space size="s32" />
        <VatDeclarationStatusesInfo />
      </ModuleLayout>
    </SkatModule>
  )
}
